import React from 'react';
import Icon from '../../../components/AppIcon';

const KPIMetricsSection = () => {
  const kpiMetrics = [
    {
      id: 'carbon-reduction',
      title: 'Carbon Emissions Reduction',
      value: '23.7%',
      target: '25%',
      change: '+2.3%',
      trend: 'up',
      status: 'success',
      icon: 'TrendingDown',
      description: 'YoY reduction vs 2023 baseline',
      progress: 94.8,
      unit: 'tCO2e',
      absolute: '1,247'
    },
    {
      id: 'energy-efficiency',
      title: 'Energy Efficiency Gains',
      value: '18.4%',
      target: '20%',
      change: '+1.8%',
      trend: 'up',
      status: 'warning',
      icon: 'Zap',
      description: 'Energy per unit produced',
      progress: 92.0,
      unit: 'kWh/unit',
      absolute: '847'
    },
    {
      id: 'waste-diversion',
      title: 'Waste Diversion Rate',
      value: '87.2%',
      target: '90%',
      change: '+4.1%',
      trend: 'up',
      status: 'success',
      icon: 'Recycle',
      description: 'Waste diverted from landfills',
      progress: 96.9,
      unit: 'tons',
      absolute: '2,156'
    },
    {
      id: 'compliance-score',
      title: 'Compliance Score',
      value: '94.2%',
      target: '95%',
      change: '-0.8%',
      trend: 'down',
      status: 'warning',
      icon: 'Shield',
      description: 'Regulatory compliance rating',
      progress: 99.2,
      unit: 'score',
      absolute: '94.2'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-success';
      case 'warning': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'success': return 'bg-success/10';
      case 'warning': return 'bg-warning/10';
      case 'error': return 'bg-error/10';
      default: return 'bg-muted/10';
    }
  };

  const getTrendIcon = (trend) => {
    return trend === 'up' ? 'TrendingUp' : 'TrendingDown';
  };

  const getTrendColor = (trend, status) => {
    if (status === 'success') return 'text-success';
    if (status === 'warning') return 'text-warning';
    if (status === 'error') return 'text-error';
    return trend === 'up' ? 'text-success' : 'text-error';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
      {kpiMetrics?.map((metric) => (
        <div key={metric?.id} className="bg-card border border-border rounded-lg p-6 hover-lift">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className={`flex items-center justify-center w-12 h-12 rounded-lg ${getStatusBg(metric?.status)}`}>
              <Icon name={metric?.icon} size={24} color={`var(--color-${metric?.status})`} />
            </div>
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getTrendColor(metric?.trend, metric?.status)} bg-muted/50`}>
              <Icon name={getTrendIcon(metric?.trend)} size={12} />
              <span>{metric?.change}</span>
            </div>
          </div>

          {/* Main Value */}
          <div className="mb-3">
            <h3 className="text-sm font-medium text-muted-foreground mb-1">{metric?.title}</h3>
            <div className="flex items-baseline space-x-2">
              <span className="text-3xl font-bold text-foreground">{metric?.value}</span>
              <span className="text-sm text-muted-foreground">of {metric?.target}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">{metric?.description}</p>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Progress to Target</span>
              <span>{metric?.progress?.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  metric?.status === 'success' ? 'bg-success' : 
                  metric?.status === 'warning' ? 'bg-warning' : 'bg-error'
                }`}
                style={{ width: `${Math.min(metric?.progress, 100)}%` }}
              ></div>
            </div>
          </div>

          {/* Absolute Value */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Absolute Value</span>
            <span className="font-medium text-foreground">{metric?.absolute} {metric?.unit}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default KPIMetricsSection;